import type {
  AnnotatedJsonSchema,
  EntityImportAnnotation,
} from '@web-config-app/core';

/**
 * Creates a type guard predicate that checks if a schema node has an
 * `x-entity-import` annotation with the specified replacement kind.
 *
 * @param replace - The import annotation replacement kind to check for (e.g., 'tenantId')
 * @returns A predicate function that can be used with findSchemaPathsWhere
 *
 * @example
 * ```typescript
 * import { findSchemaPathsWhere, hasEntityImportAnnotation } from '@web-config-app/schema-utils';
 *
 * // Find all paths that need tenant ID replacement during import
 * const tenantScopedPaths = findSchemaPathsWhere(
 *   schema,
 *   hasEntityImportAnnotation('tenantId')
 * );
 * ```
 */
export const hasEntityImportAnnotation =
  (replace: EntityImportAnnotation['replace'][number]) =>
  (
    schema: AnnotatedJsonSchema,
  ): schema is AnnotatedJsonSchema & {
    'x-entity-import': EntityImportAnnotation;
  } =>
    (() => {
      const replaceKinds = schema['x-entity-import']?.replace;
      return Array.isArray(replaceKinds) && replaceKinds.includes(replace);
    })();
